import { type ModalProps, Select as AntdSelect, Form } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type LanguagePackOptionDataFragment } from '../../../api/fragments/LanguagePackOptionData';
import { useThemeComponents } from '../../../themes';
import { Select } from '../SelectField';

type AddTranslationModalProps = {
    languages: LanguagePackOptionDataFragment[];
    onClose: ModalProps['onCancel'];
    visible: boolean;
    onAdd: (languagePackId: string) => void;
};

const AddTranslationModal = ({ languages, visible, onClose, onAdd }: AddTranslationModalProps) => {
    const { t } = useTranslation(['translationField']);
    const [value, setValue] = useState<string | null>(null);
    const { Modal } = useThemeComponents();

    useEffect(() => {
        setValue(languages?.length ? languages[0].id : null);
    }, [languages, setValue]);

    return (
        <Modal
            cancelText={t('translationField:addModal.cancelText')}
            okText={t('translationField:addModal.okText')}
            onCancel={onClose}
            onOk={() => {
                if (value) {
                    onAdd(value);
                }
            }}
            title={t('translationField:addModal.title')}
            visible={visible}
            destroyOnClose
        >
            <Form layout="vertical">
                <Form.Item label={t('translationField:addModal.fieldLabel')}>
                    <Select onChange={setValue} value={value} showSearch>
                        {languages.map(language => (
                            <AntdSelect.Option key={language.id} value={language.id}>
                                {language.referenceName}
                            </AntdSelect.Option>
                        ))}
                    </Select>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default AddTranslationModal;

export const useAddTranslationModal = (
    languages: AddTranslationModalProps['languages'],
    onAdd: AddTranslationModalProps['onAdd']
) => {
    const [visible, setVisible] = useState(false);

    const actions = useMemo(() => {
        const close = () => setVisible(false);

        return {
            open: () => setVisible(true),
            close,
            add: (languagePackId: string) => {
                onAdd(languagePackId);
                close();
            },
        };
    }, [setVisible, onAdd]);

    return {
        ...actions,
        render: () => (
            <AddTranslationModal languages={languages} onAdd={actions.add} onClose={actions.close} visible={visible} />
        ),
    };
};
